"use client";

import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { useAuth } from "@/hooks/useAuth";
import GradientAvatar from "@/app/components/GradientAvatar";
import AnimatedNumber from "@/app/components/AnimatedNumber";
import type { Dispute } from "../types";
import { marketPageUrl } from "@/lib/source-url";
import {
  decodeOutcome,
  getOutcomeColor,
  getOutcomeTextColor,
  getOutcomeBadgeClass,
  formatDecimalAmount,
  formatBond,
  truncateAddress,
  timeAgo,
  STATE_COLORS,
  formatVolume,
} from "../utils";
import CountdownTimer from "./CountdownTimer";
import { useT } from "@/hooks/useT";
import Tooltip from "@/app/components/Tooltip";

/** Bar that animates from 0% to target width on mount */
function AnimatedBar({ pct, className, rounded = true }: { pct: number; className: string; rounded?: boolean }) {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const raf = requestAnimationFrame(() => setWidth(pct));
    return () => cancelAnimationFrame(raf);
  }, [pct]);
  return (
    <div
      className={`h-full transition-all duration-700 ease-out ${rounded ? "rounded-[5px]" : ""} ${className}`}
      style={{ width: `${width}%` }}
    />
  );
}

// Use the shared Tooltip (portal + viewport-edge detection) so long ES
// text doesn't clip off-screen — the previous native `title` attribute
// also worked but lacked styling consistency with the rest of the page.
function InfoTip({ text }: { text: string }) {
  return (
    <Tooltip content={text} position="top" maxWidth={240}>
      <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-[5px] text-[9px] leading-none font-medium bg-gray-100/80 dark:bg-[#0f0f12] text-gray-400 dark:text-gray-500 cursor-help ml-1">
        i
      </span>
    </Tooltip>
  );
}

function DescriptionBlock({ text }: { text: string }) {
  const t = useT();
  const [expanded, setExpanded] = useState(false);
  const isLong = text.length > 120;
  return (
    <div className="text-[11px] text-gray-500 dark:text-gray-400 leading-relaxed break-all">
      <span className={expanded ? "" : "line-clamp-2"}>{text}</span>
      {isLong && (
        <div className="flex justify-end mt-0.5">
          <button
            onClick={(e) => { e.stopPropagation(); setExpanded(v => !v); }}
            className="flex items-center gap-0.5 text-[10px] font-medium text-gray-600 dark:text-white hover:text-gray-900 dark:hover:text-gray-300 transition-colors"
          >
            <svg className={`w-3 h-3 transition-transform ${expanded ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
            {expanded ? t("uma.card.showLess") : t("uma.card.showMore")}
          </button>
        </div>
      )}
    </div>
  );
}

interface DisputeCardProps {
  dispute: Dispute;
  currentPhase?: "commit" | "reveal";
  onClick?: () => void;
  isSelected?: boolean;
  advanced?: boolean;
}

export default function DisputeCard({ dispute, currentPhase, onClick, isSelected, advanced }: DisputeCardProps) {
  const t = useT();
  const { isLoggedIn: authenticated, isReady: authReady } = useAuth();
  const title = dispute.resolvedTitle || dispute.pmTitle || dispute.question || "Unknown Dispute";
  const stateClass = STATE_COLORS[dispute.state] || STATE_COLORS.Settled;
  const vs = dispute.voteSummary;
  const isActive = dispute.dvmStatus === "active";
  const isUpcoming = dispute.dvmStatus === "upcoming";
  const isCommitPhase = isActive && currentPhase === "commit";
  // Non-logged users see the advanced sections blurred
  const showAdvanced = advanced;
  // Always apply blur when not authenticated (even when collapsed) so it's
  // already active before the grid expansion animation starts — avoids a flash
  const blurAdvanced = authReady && !authenticated;

  // Calculate total tokens for vote bars
  const totalVoteTokens = vs?.groups.reduce(
    (sum, g) => sum + parseFloat(g.totalVoteAmount || "0"),
    0
  ) || 0;

  return (
    <div
      onClick={onClick}
      className={`relative flex flex-col min-h-64 bg-white dark:bg-[#18181D] border rounded-[5px] overflow-hidden transition-colors cursor-pointer ${
        isSelected
          ? "border-gray-400 dark:border-gray-600 ring-1 ring-gray-300 dark:ring-gray-600"
          : "border-gray-200 dark:border-[#1A1A1A] hover:border-gray-300/80 dark:hover:border-white/[0.10]"
      }`}
    >
      {/* Background image with gradient mask — matches EventCard */}
      {dispute.pmImage && (
        <div
          className="absolute top-0 left-0 right-0 h-[200px] pointer-events-none"
          style={{
            backgroundImage: `url(${dispute.pmImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'top',
            maskImage: 'linear-gradient(to bottom, rgba(0,0,0,0.15) 0%, transparent 100%)',
            WebkitMaskImage: 'linear-gradient(to bottom, rgba(0,0,0,0.15) 0%, transparent 100%)',
          }}
        />
      )}

      {/* Header: Image + Title + Badges */}
      <div className="p-4 pb-3">
        <div className="flex gap-3 items-start">
          {/* Image thumbnail + timeAgo below — like EventCard */}
          {dispute.pmImage && (
            <div className="flex flex-col items-center gap-1.5 flex-shrink-0">
              <div className="relative w-[60px] h-[60px] rounded-[5px] overflow-hidden bg-gray-100 dark:bg-[#111] ring-1 ring-black/5 dark:ring-white/5">
                <img
                  src={dispute.pmImage}
                  alt=""
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          )}

          <div className="flex-1 min-w-0">
            {/* Badges row */}
            <div className="flex items-center justify-between gap-1.5 mb-1.5">
              <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                <span className={`text-[11px] font-medium px-2 py-0.5 rounded-[5px] ${stateClass}`}>
                  {dispute.state}
                </span>
                {isActive && (
                  <Tooltip content={isCommitPhase ? t("uma.card.tip.commitPhase") : t("uma.card.tip.revealPhase")} position="top" maxWidth={260}>
                    <span
                      className={`text-[11px] font-medium px-2 py-0.5 rounded-[5px] ${
                        isCommitPhase
                          ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                          : "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                      }`}
                    >
                      {isCommitPhase ? t("uma.card.commitPhase") : t("uma.card.revealPhase")}
                    </span>
                  </Tooltip>
                )}
                {isUpcoming && (
                  <Tooltip content={t("uma.card.tip.upcoming")} position="top" maxWidth={260}>
                    <span className="text-[11px] font-medium px-2 py-0.5 rounded-[5px] bg-gray-100 text-gray-500 dark:bg-gray-800/50 dark:text-gray-400">
                      {t("uma.card.pendingVote")}
                    </span>
                  </Tooltip>
                )}
                {vs?.rollCount != null && vs.rollCount > 0 && (
                  <Tooltip content={t("uma.card.voteRound", { n: vs.rollCount + 1 })} position="top" maxWidth={260}>
                    <span className="text-[11px] font-medium px-2 py-0.5 rounded-[5px] bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">
                      {t("uma.card.round", { n: vs.rollCount + 1 })}
                    </span>
                  </Tooltip>
                )}
                {!dispute.pmImage && !isUpcoming && isCommitPhase && (
                  <span className="text-[10px] text-gray-400 dark:text-gray-500">{timeAgo(dispute.requestTime)}</span>
                )}
              </div>
              {/* Volume badge — fixed right, never wraps with status badges */}
              {(dispute.pmVolume != null && dispute.pmVolume > 0) && (
                <span className="flex-shrink-0 text-[10px] font-medium px-2 py-0.5 rounded-[5px] bg-gray-100 text-gray-600 dark:bg-gray-800/50 dark:text-gray-400">
                  {formatVolume(dispute.pmVolume)}
                </span>
              )}
            </div>

            {/* Title — like EventCard font-satoshi font-black */}
            {dispute.pmSlug ? (
              <Link
                href={marketPageUrl({
                  marketSlug: dispute.pmSlug,
                  eventSlug: dispute.polymarketEventSlug,
                  source: 'polymarket',
                })}
                prefetch={false}
                className="font-satoshi font-black text-[13px] text-gray-900 dark:text-white leading-snug line-clamp-2 hover:opacity-80 transition-colors"
                onClick={e => e.stopPropagation()}
              >
                {title}
              </Link>
            ) : (
              <p className="font-satoshi font-black text-[13px] text-gray-900 dark:text-white leading-snug line-clamp-2">
                {title}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Content body */}
      <div className="px-4 pb-3 flex flex-col gap-2.5 flex-1">
        {/* Description */}
        {dispute.resolvedDescription && (() => {
          // Strip raw metadata (initializer, ooRequester, childRequester, etc.)
          const clean = dispute.resolvedDescription!
            .replace(/,?(?:initializer|ooRequester|childRequester|childChainId):[a-f0-9]+/gi, '')
            .replace(/\s*,\s*$/, '')
            .trim();
          return clean ? <DescriptionBlock text={clean} /> : null;
        })()}

        {/* Proposed Outcome — pill with progress bar */}
        {(() => {
          const match = vs?.groups.find(g => g.price === dispute.proposedPrice);
          const pct = match && totalVoteTokens > 0
            ? (parseFloat(match.totalVoteAmount || "0") / totalVoteTokens) * 100
            : 0;
          return (
            <div className={`relative flex items-center justify-between px-3 py-2 rounded-[5px] overflow-hidden ${getOutcomeBadgeClass(dispute.proposedPrice)}`}>
              {/* Progress fill */}
              {pct > 0 && (
                <div
                  className={`absolute inset-y-0 left-0 ${getOutcomeColor(dispute.proposedPrice)} opacity-20 transition-all duration-700 ease-out`}
                  style={{ width: `${pct}%` }}
                />
              )}
              <span className="relative text-xs font-medium opacity-70">{t("uma.card.proposed")}</span>
              <span className="relative text-sm font-bold">{decodeOutcome(dispute.proposedPrice)}</span>
              {pct > 0 && (
                <span className="relative text-xs font-medium opacity-70 tabular-nums"><AnimatedNumber value={Math.round(pct)} />%</span>
              )}
            </div>
          );
        })()}

        {/* Vote Info — Commit phase */}
        {vs && isCommitPhase && (vs.committersCount > 0 || parseFloat(vs.totalTokensCommitted || "0") > 0) && (
          <div className="space-y-1.5">
            <div className="flex items-center justify-between text-[11px]">
              <span className="text-gray-500 dark:text-gray-400">{t("uma.card.commitStage")}</span>
              <span className="text-gray-500 dark:text-gray-400 tabular-nums">
                <AnimatedNumber value={vs.committersCount} /> {vs.committersCount !== 1 ? t("uma.card.votes") : t("uma.card.vote")}
              </span>
            </div>
            {(() => {
              const committed = parseFloat(vs.totalTokensCommitted || "0");
              const stake = parseFloat(vs.cumulativeStakeAtRound || "0");
              const pct = stake > 0 ? Math.min((committed / stake) * 100, 100) : 0;
              return (
                <div className="w-full h-1.5 rounded-[5px] bg-gray-100/80 dark:bg-[#0f0f12] overflow-hidden">
                  <AnimatedBar pct={pct} className="bg-blue-500" />
                </div>
              );
            })()}
          </div>
        )}


        {/* Dispute time — when was it disputed (useful for upcoming to gauge when voting starts) */}
        {dispute.disputeTime && (
          <div className="flex items-center justify-between text-[11px]">
            <span className="text-gray-400 dark:text-gray-500">{t("uma.card.disputed")}</span>
            <span className="text-gray-500 dark:text-gray-400">{timeAgo(dispute.disputeTime)}</span>
          </div>
        )}

        {/* GAT + commit stats — commit phase, advanced only, slide animation */}
        {vs && isCommitPhase && (
          <div
            className="grid transition-[grid-template-rows] duration-300 ease-in-out"
            style={{ gridTemplateRows: showAdvanced ? "1fr" : "0fr" }}
          >
            <div className={`overflow-hidden ${blurAdvanced ? "blur-[6px] select-none pointer-events-none" : ""}`}>
              <div className="space-y-2">
                {/* GAT (Quorum) bar */}
                {(() => {
                  const committed = parseFloat(vs.totalTokensCommitted || "0");
                  if (committed <= 0) return null;
                  const gat = parseFloat(vs.minParticipationRequirement || "0") || 5_000_000;
                  const pct = Math.min((committed / gat) * 100, 100);
                  const passed = committed >= gat;
                  return (
                    <div className="space-y-1">
                      <div className="flex items-center justify-between text-[10px] gap-1">
                        <span className="text-gray-500 dark:text-gray-400 whitespace-nowrap">{t("uma.card.quorum")}<InfoTip text={t("uma.card.tip.quorumCommit")} /></span>
                        <span className={`font-medium tabular-nums whitespace-nowrap ${passed ? "text-green-600 dark:text-green-400" : "text-orange-600 dark:text-orange-400"}`}>
                          {formatDecimalAmount(vs.totalTokensCommitted)} / {formatDecimalAmount(String(gat))}
                          {passed && " \u2713"}
                        </span>
                      </div>
                      <div className="w-full h-1 rounded-[5px] bg-gray-100/80 dark:bg-[#0f0f12] overflow-hidden">
                        <AnimatedBar pct={pct} className={passed ? "bg-green-500" : "bg-orange-500"} />
                      </div>
                    </div>
                  );
                })()}

              </div>
            </div>
          </div>
        )}

        {/* Vote Breakdown — Reveal phase */}
        {vs && vs.groups.length > 0 && (
          <div className="space-y-1.5">
            <div className="flex items-center justify-between text-[11px]">
              <span className="text-gray-500 dark:text-gray-400">{t("uma.card.voteBreakdown")}</span>
              <span className="text-gray-500 dark:text-gray-400 tabular-nums">
                <AnimatedNumber value={vs.votersAmount} /> {vs.votersAmount !== 1 ? t("uma.card.voters") : t("uma.card.voter")}
              </span>
            </div>
            <div className="w-full h-1.5 rounded-[5px] bg-gray-100/80 dark:bg-[#0f0f12] overflow-hidden flex">
              {vs.groups
                .sort((a, b) => parseFloat(b.totalVoteAmount || "0") - parseFloat(a.totalVoteAmount || "0"))
                .map((group, i) => {
                  const groupTokens = parseFloat(group.totalVoteAmount || "0");
                  const pct = totalVoteTokens > 0 ? (groupTokens / totalVoteTokens) * 100 : 0;
                  if (pct < 0.5) return null;
                  return (
                    <AnimatedBar
                      key={i}
                      pct={pct}
                      rounded={false}
                      className={`${getOutcomeColor(group.price)} ${i > 0 ? "ml-px" : ""}`}
                    />
                  );
                })}
            </div>
            <div className="flex flex-wrap gap-x-3 gap-y-0.5">
              {vs.groups
                .sort((a, b) => parseFloat(b.totalVoteAmount || "0") - parseFloat(a.totalVoteAmount || "0"))
                .map((group, i) => {
                  const groupTokens = parseFloat(group.totalVoteAmount || "0");
                  const pct = totalVoteTokens > 0 ? (groupTokens / totalVoteTokens) * 100 : 0;
                  const isWinner = group.won || (pct >= 99 && groupTokens > 0);
                  return (
                    <div key={i} className={`flex items-center gap-1 ${isWinner ? "px-1.5 py-0.5 rounded-[5px] bg-amber-50 dark:bg-amber-900/20" : ""}`}>
                      {isWinner
                        ? <span className="text-[10px]">👑</span>
                        : <div className={`w-1.5 h-1.5 rounded-[5px] ${getOutcomeColor(group.price)}`} />
                      }
                      <span className={`text-[10px] font-medium tabular-nums ${isWinner ? getOutcomeTextColor(group.price) : "text-gray-600 dark:text-gray-400"}`}>
                        {decodeOutcome(group.price)}
                      </span>
                      <span className={`text-[10px] tabular-nums ${isWinner ? "font-bold text-amber-600 dark:text-amber-400" : "text-gray-600 dark:text-gray-400"}`}>
                        <AnimatedNumber value={Math.round(pct)} />%
                      </span>
                    </div>
                  );
                })}
            </div>
          </div>
        )}

        {/* Reveals + GAT + SPAT — advanced mode with slide animation */}
        {vs && isActive && !isCommitPhase && (
          <div
            className="grid transition-[grid-template-rows] duration-300 ease-in-out"
            style={{ gridTemplateRows: showAdvanced ? "1fr" : "0fr" }}
          >
            <div className={`overflow-hidden ${blurAdvanced ? "blur-[6px] select-none pointer-events-none" : ""}`}>
              <div className="space-y-2.5">
                {/* Reveal Progress — how many commits have been revealed */}
                {vs.committersCount > 0 && (() => {
                  const revealed = vs.votersAmount;
                  const committed = vs.committersCount;
                  const pct = committed > 0 ? Math.min((revealed / committed) * 100, 100) : 0;
                  return (
                    <div className="space-y-1">
                      <div className="flex items-center justify-between text-[11px]">
                        <span className="text-gray-500 dark:text-gray-400">{t("uma.card.reveals")}<InfoTip text={t("uma.card.tip.reveals")} /></span>
                        <span className="text-gray-500 dark:text-gray-400 tabular-nums">
                          <AnimatedNumber value={revealed} />/{committed} (<AnimatedNumber value={Math.round(pct)} />%)
                        </span>
                      </div>
                      <div className="w-full h-1 rounded-[5px] bg-gray-100/80 dark:bg-[#0f0f12] overflow-hidden">
                        <AnimatedBar pct={pct} className="bg-green-500" />
                      </div>
                    </div>
                  );
                })()}
                {/* GAT (Quorum) */}
                {(() => {
                  const gat = parseFloat(vs.minParticipationRequirement || "0");
                  const revealed = parseFloat(vs.totalVotesRevealed || "0");
                  if (gat <= 0) return null;
                  const pct = Math.min((revealed / gat) * 100, 100);
                  const passed = revealed >= gat;
                  return (
                    <div className="space-y-1">
                      <div className="flex items-center justify-between text-[10px] gap-1">
                        <span className="text-gray-500 dark:text-gray-400 whitespace-nowrap">{t("uma.card.quorum")}<InfoTip text={t("uma.card.tip.quorumReveal")} /></span>
                        <span className={`font-medium tabular-nums whitespace-nowrap ${passed ? "text-green-600 dark:text-green-400" : "text-orange-600 dark:text-orange-400"}`}>
                          {formatDecimalAmount(vs.totalVotesRevealed)} / {formatDecimalAmount(vs.minParticipationRequirement!)}
                          {passed && " \u2713"}
                        </span>
                      </div>
                      <div className="w-full h-1 rounded-[5px] bg-gray-100/80 dark:bg-[#0f0f12] overflow-hidden">
                        <AnimatedBar pct={pct} className={passed ? "bg-green-500" : "bg-orange-500"} />
                      </div>
                    </div>
                  );
                })()}

                {/* SPAT (Consensus) */}
                {vs.groups.length > 0 && (() => {
                  const spat = parseFloat(vs.minAgreementRequirement || "0");
                  if (spat <= 0) return null;
                  const topGroup = vs.groups.reduce((a, b) =>
                    parseFloat(b.totalVoteAmount || "0") > parseFloat(a.totalVoteAmount || "0") ? b : a
                  );
                  const topTokens = parseFloat(topGroup.totalVoteAmount || "0");
                  const pct = Math.min((topTokens / spat) * 100, 100);
                  const passed = topTokens >= spat;
                  return (
                    <div className="space-y-1">
                      <div className="flex items-center justify-between text-[10px] gap-1">
                        <span className="text-gray-500 dark:text-gray-400 whitespace-nowrap">{t("uma.card.consensus")}<InfoTip text={t("uma.card.tip.consensus")} /></span>
                        <span className={`font-medium tabular-nums whitespace-nowrap ${passed ? "text-green-600 dark:text-green-400" : "text-orange-600 dark:text-orange-400"}`}>
                          {formatDecimalAmount(String(topTokens))} / {formatDecimalAmount(vs.minAgreementRequirement!)}
                          {passed && " \u2713"}
                        </span>
                      </div>
                      <div className="w-full h-1 rounded-[5px] bg-gray-100/80 dark:bg-[#0f0f12] overflow-hidden">
                        <AnimatedBar pct={pct} className={passed ? "bg-green-500" : "bg-orange-500"} />
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 pb-3 pt-1 flex flex-col gap-2">

        {/* Proposal Expiration Countdown */}
        {dispute.state === "Proposed" && dispute.proposalExpirationTimestamp && (
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-500 dark:text-gray-400">{t("uma.card.expiresIn")}</span>
            <span className="font-medium text-orange-600 dark:text-orange-400 tabular-nums">
              <CountdownTimer endTime={dispute.proposalExpirationTimestamp} />
            </span>
          </div>
        )}

        {/* Proposer / Disputer — 1 row on mobile (no avatars), 2 rows on desktop */}
        {((dispute.proposer && dispute.proposer.length >= 10) || (dispute.disputer && dispute.disputer.length >= 10)) && (
          <div className="flex items-center justify-between gap-1.5 text-[11px] pt-1.5 border-t border-gray-200 dark:border-[#1A1A1A]">
            {dispute.proposer && dispute.proposer.length >= 10 && (
              <span className="flex items-center gap-1 min-w-0" onClick={e => e.stopPropagation()}>
                <GradientAvatar seed={dispute.proposer} size={16} className="rounded-[5px] ring-1 ring-black/5 dark:ring-white/10 hidden sm:block flex-shrink-0" />
                <span className="text-[9px] font-medium leading-none px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400 flex-shrink-0">{t("uma.card.proposer")}</span>
                <a
                  href={`https://polygonscan.com/address/${dispute.proposer}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${dispute.proposerEns ? '' : 'font-mono '}text-[10px] leading-none text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors truncate`}
                >
                  {dispute.proposerEns || truncateAddress(dispute.proposer)}
                </a>
              </span>
            )}
            {dispute.disputer && dispute.disputer.length >= 10 && (
              <span className="flex items-center gap-1 min-w-0" onClick={e => e.stopPropagation()}>
                <GradientAvatar seed={dispute.disputer} size={16} className="rounded-[5px] ring-1 ring-black/5 dark:ring-white/10 hidden sm:block flex-shrink-0" />
                <span className="text-[9px] font-medium leading-none px-1.5 py-0.5 rounded bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400 flex-shrink-0">{t("uma.card.disputer")}</span>
                <a
                  href={`https://polygonscan.com/address/${dispute.disputer}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${dispute.disputerEns ? '' : 'font-mono '}text-[10px] leading-none text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors truncate`}
                >
                  {dispute.disputerEns || truncateAddress(dispute.disputer)}
                </a>
              </span>
            )}
          </div>
        )}

        {/* Settlement section */}
        {(dispute.state === "Settled" || dispute.state === "Resolved") && dispute.settlementPrice && !isActive && !isUpcoming && (
          <div className="flex items-center justify-between text-xs pt-1 border-t border-gray-200 dark:border-[#1A1A1A]">
            <span className="text-gray-500 dark:text-gray-400">{t("uma.card.result")}</span>
            <div className="flex items-center gap-2">
              <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded ${getOutcomeBadgeClass(dispute.settlementPrice)}`}>
                {decodeOutcome(dispute.settlementPrice)}
              </span>
              {dispute.state === "Settled" && dispute.settlementPayout && (
                <span className="font-medium text-green-600 dark:text-green-400">
                  +{formatBond(dispute.settlementPayout) || "—"}
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
